import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useApi } from "../api";
import type { Initiative } from "../types";
import { ErrorNotice } from "./Ui";

export function DependencyNotes({
  item,
  editable,
}: {
  item: Initiative;
  editable: boolean;
}) {
  return (
    <div>
      <p className="section-help">
        Upstream links are entered in Edit initiative and checked for cycles.
        The map is derived from those links, not imported from Linear.
        Administrators can record the type and delay risk for each relationship.
      </p>
      {item.dependencies?.map((edge) => (
        <div key={`${edge.id}-${item.version}`}>
          <strong>SPD-PA-{String(edge.id).padStart(3, "0")}</strong>
          {editable ? (
            <DependencyNoteForm item={item} edge={edge} />
          ) : (
            <p>
              {edge.dependencyType || "Type not recorded"} ·{" "}
              {edge.risk || "Delay risk not recorded"}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
function DependencyNoteForm({
  item,
  edge,
}: {
  item: Initiative;
  edge: NonNullable<Initiative["dependencies"]>[number];
}) {
  const api = useApi();
  const client = useQueryClient();
  const [type, setType] = useState(edge.dependencyType ?? "");
  const [risk, setRisk] = useState(edge.risk ?? "");
  const [reason, setReason] = useState("");
  const save = useMutation({
    mutationFn: () =>
      api.put(`/initiatives/${item.publicId}/dependency-notes`, {
        dependencyId: edge.id,
        dependencyType: type,
        risk,
        expectedVersion: item.version,
        changeReason: reason,
      }),
    onSuccess: () => client.invalidateQueries(),
  });
  return (
    <details>
      <summary>Edit dependency type and delay risk</summary>
      <form
        className="portfolio-form"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <label>
          Dependency type
          <input
            value={type}
            maxLength={120}
            onChange={(e) => setType(e.target.value)}
            placeholder="Regulatory, technical, commercial…"
          />
        </label>
        <label>
          Risk if delayed
          <textarea
            value={risk}
            maxLength={4000}
            onChange={(e) => setRisk(e.target.value)}
          />
        </label>
        <label>
          Change reason
          <input
            required
            minLength={3}
            maxLength={1000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
        <button
          className="btn-primary"
          disabled={save.isPending || reason.trim().length < 3}
        >
          {save.isPending ? "Saving…" : "Save dependency notes"}
        </button>
        {save.isError && <ErrorNotice error={save.error} />}
      </form>
    </details>
  );
}
